import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  deleteDoc,
  getDocs,
  runTransaction,
  arrayUnion,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { loanReminderKey, reminderAlreadySent } from '../utils/loanReminderKey';

const LOAN_REMINDER_MARKS_COLLECTION = 'loanReminderMarks';

export interface Notification {
  id?: string;
  userId: string;
  type: 'guest_invite' | 'event_update' | 'rsvp_change' | 'general' | 'recipe_share' | 'allergy_warning' | 'event_reminder' | 'new_follower' | 'post_like' | 'post_comment' | 'post_mention' | 'post_share';
  title: string;
  message: string;
  read: boolean;
  createdAt: Date;
  senderId?: string;
  senderName?: string;
  metadata?: {
    eventId?: string;
    guestId?: string;
    recipeData?: any;
    postId?: string;
    commentId?: string;
    profilePictureUrl?: string;
    [key: string]: any;
  };
}

const NOTIFICATIONS_COLLECTION = 'notifications';

/**
 * Cria uma nova notificação
 */
export const createNotification = async (
  notification: Omit<Notification, 'id' | 'createdAt' | 'read'>
): Promise<string> => {
  try {
    const notificationData = {
      ...notification,
      read: false,
      createdAt: Timestamp.now(),
    };

    const docRef = await addDoc(
      collection(db, NOTIFICATIONS_COLLECTION),
      notificationData
    );

    return docRef.id;
  } catch (error) {
    console.error('Erro ao criar notificação:', error);
    throw error;
  }
};

/**
 * Envia notificação para um convidado (se ele for usuário do Hostly)
 */
export type LoanReminderResult = 'sent' | 'already' | 'no_account' | 'error';

export const sendLoanReminder = async (
  borrowerEmail: string,
  senderId: string,
  senderName: string,
  itemName: string,
  message: string,
  loan?: { itemId?: string; loanId?: string; loanedAt?: string; operationId?: string }
): Promise<LoanReminderResult> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', borrowerEmail.toLowerCase()), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return 'no_account';

    const recipientId = snapshot.docs[0].id;
    const markRef = doc(
      db,
      LOAN_REMINDER_MARKS_COLLECTION,
      loanReminderKey({
        ownerId: senderId,
        itemId: loan?.itemId,
        loanId: loan?.loanId,
        loanedAt: loan?.loanedAt,
        recipientId,
      })
    );

    let created = false;
    await runTransaction(db, async (tx) => {
      const markSnap = await tx.get(markRef);
      if (markSnap.exists() && reminderAlreadySent(markSnap.data() as any, loan?.operationId)) {
        return;
      }
      const notifRef = doc(collection(db, NOTIFICATIONS_COLLECTION));
      tx.set(notifRef, {
        userId: recipientId,
        type: 'general',
        title: `Devolução: ${itemName}`,
        message,
        read: false,
        createdAt: Timestamp.now(),
        senderId,
        senderName,
        metadata: {
          loanId: loan?.loanId || '',
          itemId: loan?.itemId || '',
        },
      });
      tx.set(
        markRef,
        {
          ownerId: senderId,
          itemId: loan?.itemId || '',
          loanId: loan?.loanId || '',
          recipientId,
          sentOperationIds: arrayUnion(loan?.operationId || ''),
          updatedAt: Timestamp.now(),
        },
        { merge: true }
      );
      created = true;
    });
    return created ? 'sent' : 'already';
  } catch (error) {
    console.error('Erro ao enviar lembrete de devolução:', error);
    return 'error';
  }
};

export const sendGuestNotification = async (
  guestEmail: string,
  senderId: string,
  senderName: string,
  message: string,
  metadata?: any
): Promise<boolean> => {
  try {
    // Buscar usuário pelo email
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', guestEmail.toLowerCase()), limit(1));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      // Usuário não encontrado, não é usuário do Hostly
      return false;
    }

    const userDoc = querySnapshot.docs[0];
    const userId = userDoc.id;

    // Criar notificação
    await createNotification({
      userId,
      type: 'guest_invite',
      title: 'Novo Convite',
      message,
      senderId,
      senderName,
      metadata,
    });

    // Notificação é criada no Firestore
    // O Cloud Function 'sendPushNotification' ouvirá a criação e enviará o push automaticamente via FCM

    return true;
  } catch (error) {
    console.error('Erro ao enviar notificação para convidado:', error);
    return false;
  }
};

/**
 * Verifica se um email pertence a um usuário do Hostly
 */
export const isHostlyUser = async (email: string): Promise<boolean> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Erro ao verificar usuário:', error);
    return false;
  }
};

/**
 * Busca notificações de um usuário
 * Usar índice composto: userId (ASC) + createdAt (DESC)
 */
export const getUserNotifications = (
  userId: string,
  limitCount: number = 50,
  callback: (notifications: Notification[]) => void
) => {
  const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
  const q = query(
    notificationsRef,
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const notifications: Notification[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        notifications.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
        } as Notification);
      });
      callback(notifications);
    },
    (error) => {
      console.error('Erro ao escutar notificações:', error);
      callback([]);
    }
  );
};

/**
 * Busca apenas notificações não lidas
 * Usar índice composto: userId (ASC) + read (ASC) + createdAt (DESC)
 */
export const getUnreadNotifications = (
  userId: string,
  callback: (notifications: Notification[]) => void
) => {
  const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
  const q = query(
    notificationsRef,
    where('userId', '==', userId),
    where('read', '==', false),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const notifications: Notification[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        notifications.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
        } as Notification);
      });
      callback(notifications);
    },
    (error) => {
      console.error('Erro ao escutar notificações não lidas:', error);
      callback([]);
    }
  );
};

/**
 * Conta notificações não lidas
 */
export const getUnreadCount = async (userId: string): Promise<number> => {
  try {
    const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      where('read', '==', false)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Erro ao contar notificações não lidas:', error);
    return 0;
  }
};

/**
 * Marca uma notificação como lida
 */
export const markAsRead = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await updateDoc(notificationRef, {
      read: true,
    });
  } catch (error) {
    console.error('Erro ao marcar notificação como lida:', error);
    throw error;
  }
};

/**
 * Marca todas as notificações como lidas
 */
export const markAllAsRead = async (userId: string): Promise<void> => {
  try {
    const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      where('read', '==', false)
    );
    const snapshot = await getDocs(q);

    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
      batch.update(doc.ref, { read: true });
    });

    await batch.commit();
  } catch (error) {
    console.error('Erro ao marcar todas notificações como lidas:', error);
    throw error;
  }
};

/**
 * Deleta uma notificação
 */
export const deleteNotification = async (notificationId: string): Promise<void> => {
  try {
    const notificationRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
    await deleteDoc(notificationRef);
  } catch (error) {
    console.error('Erro ao deletar notificação:', error);
    throw error;
  }
};

/**
 * Deleta todas as notificações lidas de um usuário
 */
export const deleteReadNotifications = async (userId: string): Promise<void> => {
  try {
    const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      where('read', '==', true)
    );
    const snapshot = await getDocs(q);

    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });

    await batch.commit();
  } catch (error) {
    console.error('Erro ao deletar notificações lidas:', error);
    throw error;
  }
};

/**
 * Deleta notificações antigas (mais de 30 dias)
 */
export const deleteOldNotifications = async (userId: string, daysOld: number = 30): Promise<void> => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    const notificationsRef = collection(db, NOTIFICATIONS_COLLECTION);
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      where('createdAt', '<', Timestamp.fromDate(cutoffDate))
    );
    const snapshot = await getDocs(q);

    const batch = writeBatch(db);
    snapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });

    await batch.commit();
  } catch (error) {
    console.error('Erro ao deletar notificações antigas:', error);
    throw error;
  }
};

/**
 * Envia notificação para múltiplos convidados
 */
export const sendBulkGuestNotifications = async (
  guestEmails: string[],
  senderId: string,
  senderName: string,
  message: string,
  metadata?: any
): Promise<{ sent: number; failed: number; notHostlyUsers: number }> => {
  let sent = 0;
  let failed = 0;
  let notHostlyUsers = 0;

  console.log(`Enviando notificações para ${guestEmails.length} emails:`, guestEmails);

  for (const email of guestEmails) {
    try {
      console.log(`Tentando enviar para: ${email}`);
      const success = await sendGuestNotification(
        email,
        senderId,
        senderName,
        message,
        metadata
      );

      if (success) {
        sent++;
        console.log(`✅ Enviado para: ${email}`);
      } else {
        notHostlyUsers++;
        console.log(`⚠️ ${email} não é usuário Hostly`);
      }
    } catch (error) {
      console.error(`❌ Erro ao enviar notificação para ${email}:`, error);
      failed++;
    }
  }

  console.log(`Resultado final: ${sent} enviados, ${notHostlyUsers} não usuários, ${failed} falhas`);
  return { sent, failed, notHostlyUsers };
};

export const shareRecipe = async (
  recipe: any,
  senderUid: string,
  senderName: string,
  recipientEmail: string
): Promise<boolean> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', recipientEmail.toLowerCase()), limit(1));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return false;
    }

    const userDoc = querySnapshot.docs[0];
    const recipientUserId = userDoc.id;

    await createNotification({
      userId: recipientUserId,
      type: 'recipe_share',
      title: 'Nova Receita Compartilhada',
      message: `${senderName} compartilhou a receita "${recipe.title}" com você!`,
      senderId: senderUid,
      senderName,
      metadata: {
        senderName,
        senderId: senderUid,
        recipeData: {
          title: recipe.title || '',
          ingredients: recipe.ingredients || [],
          instructions: recipe.instructions || '',
          servings: recipe.servings || 1,
          prepTime: recipe.prepTime || 0,
          imageUrl: recipe.imageUrl || '',
          videoUrl: recipe.videoUrl || '',
          description: recipe.description || '',
          category: recipe.category || '',
          rating: recipe.rating || 0,
        },
      },
    });

    return true;
  } catch (error) {
    console.error('Erro ao compartilhar receita:', error);
    return false;
  }
};

/**
 * Compartilha receita diretamente por userId
 */
export const shareRecipeByUserId = async (
  recipe: any,
  senderUid: string,
  senderName: string,
  recipientUserId: string
): Promise<boolean> => {
  try {
    await createNotification({
      userId: recipientUserId,
      type: 'recipe_share',
      title: 'Nova Receita Compartilhada',
      message: `${senderName} compartilhou a receita "${recipe.title}" com você!`,
      senderId: senderUid,
      senderName,
      metadata: {
        senderName,
        senderId: senderUid,
        recipeData: {
          title: recipe.title || '',
          ingredients: recipe.ingredients || [],
          instructions: recipe.instructions || '',
          servings: recipe.servings || 1,
          prepTime: recipe.prepTime || 0,
          imageUrl: recipe.imageUrl || '',
          videoUrl: recipe.videoUrl || '',
          description: recipe.description || '',
          category: recipe.category || '',
          rating: recipe.rating || 0,
        },
      },
    });

    return true;
  } catch (error) {
    console.error('Erro ao compartilhar receita por userId:', error);
    return false;
  }
};

/**
 * Notifica quando alguém começa a seguir o usuário
 */
export const notifyNewFollower = async (
  followerId: string,
  followerName: string,
  followerAvatar: string | undefined,
  targetUserId: string
): Promise<void> => {
  try {
    await createNotification({
      userId: targetUserId,
      type: 'new_follower',
      title: 'Novo Seguidor!',
      message: `${followerName} começou a te seguir`,
      senderId: followerId,
      senderName: followerName,
      metadata: {
        profilePictureUrl: followerAvatar,
      },
    });
  } catch (error) {
    console.error('Erro ao notificar novo seguidor:', error);
  }
};

/**
 * Notifica quando alguém curte um post
 */
export const notifyPostLike = async (
  likerId: string,
  likerName: string,
  likerAvatar: string | undefined,
  postId: string,
  postOwnerId: string
): Promise<void> => {
  // Não notificar se o usuário curtiu o próprio post
  if (likerId === postOwnerId) {
    return;
  }

  try {
    await createNotification({
      userId: postOwnerId,
      type: 'post_like',
      title: 'Nova Curtida!',
      message: `${likerName} curtiu seu post`,
      senderId: likerId,
      senderName: likerName,
      metadata: {
        postId,
        profilePictureUrl: likerAvatar,
      },
    });
  } catch (error) {
    console.error('Erro ao notificar curtida:', error);
  }
};

/**
 * Notifica quando alguém comenta em um post
 */
export const notifyPostComment = async (
  commenterId: string,
  commenterName: string,
  commenterAvatar: string | undefined,
  commentText: string,
  postId: string,
  postOwnerId: string
): Promise<void> => {
  // Não notificar se o usuário comentou no próprio post
  if (commenterId === postOwnerId) {
    return;
  }

  try {
    // Truncar comentário para preview
    const preview = commentText.length > 50
      ? commentText.substring(0, 50) + '...'
      : commentText;

    await createNotification({
      userId: postOwnerId,
      type: 'post_comment',
      title: 'Novo Comentário!',
      message: `${commenterName} comentou: "${preview}"`,
      senderId: commenterId,
      senderName: commenterName,
      metadata: {
        postId,
        profilePictureUrl: commenterAvatar,
      },
    });
  } catch (error) {
    console.error('Erro ao notificar comentário:', error);
  }
};
/**
 * Compartilha um post com outro usuário
 */
export const sharePost = async (
  postId: string,
  senderId: string,
  senderName: string,
  recipientUserId: string
): Promise<boolean> => {
  try {
    await createNotification({
      userId: recipientUserId,
      type: 'post_share',
      title: 'Post Compartilhado',
      message: `${senderName} compartilhou um post com você`,
      senderId,
      senderName,
      metadata: {
        postId,
      },
    });
    return true;
  } catch (error) {
    console.error('Erro ao compartilhar post:', error);
    return false;
  }
};
